import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ArrowRight, CalendarPlus, Download, Share2, MessageCircle, Award, Loader2 } from "lucide-react";
import { RealQR } from "@/components/RealQR";
import { publicApi } from "@/lib/publicApi";
import { MEMBER_TOKEN_KEY } from "@/lib/authApi";
import { useDemo } from "@/store/DemoStore";

const API = process.env.REACT_APP_BACKEND_URL;
const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

/** Seal that draws its own ring and tick — the one piece of motion we let people watch. */
function Seal({ sponsor }) {
  const ring = sponsor ? "var(--red)" : "var(--green)";
  const mark = sponsor ? "var(--red-2)" : "var(--green)";
  return (
    <motion.svg width="76" height="76" viewBox="0 0 76 76" fill="none" data-testid="success-seal"
      initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 180, damping: 16 }}>
      <motion.circle cx="38" cy="38" r="34" stroke={ring} strokeWidth="1.5" fill="none"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} style={{ rotate: -90, originX: "50%", originY: "50%" }} />
      <motion.circle cx="38" cy="38" r="27" stroke="var(--hairline)" strokeWidth="1" fill="none"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.6 }} />
      {sponsor ? (
        <motion.path d="M38 22l4.6 9.6 10.4 1.4-7.6 7.3 1.9 10.4L38 45.8l-9.3 4.9 1.9-10.4-7.6-7.3 10.4-1.4z"
          stroke={mark} strokeWidth="1.6" strokeLinejoin="round" fill="none"
          initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.9, ease: "easeInOut" }} />
      ) : (
        <motion.path d="M25 39.5l9 9L52 29.5" stroke={mark} strokeWidth="2.4" strokeLinecap="round" fill="none"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ delay: 0.55, duration: 0.6, ease: "easeOut" }} />
      )}
    </motion.svg>
  );
}

const rise = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.35 + i * 0.09, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

function icsHref(ev) {
  const start = (ev?.date || "").replace(/-/g, "") || "20261128";
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//BNI Davangere//Members Day//EN", "BEGIN:VEVENT",
    `UID:members-day-${start}@bnidavangere`,
    `DTSTART;VALUE=DATE:${start}`, `DTEND;VALUE=DATE:${start}`,
    `SUMMARY:${ev?.name || "BNI Davangere Members Day"}`,
    `LOCATION:${(ev?.venue || "Davangere").replace(/,/g, "\\,")}`,
    "DESCRIPTION:Your pass QR is in the Member Portal. Check in at the reception desk on arrival.",
    "END:VEVENT", "END:VCALENDAR",
  ];
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join("\r\n"))}`;
}

function escapeXml(s) {
  return String(s).replace(/[<>&'"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" }[c]));
}

/** 1080x1350 WhatsApp card — mirrors the Hero "Save WhatsApp card" visual. */
function buildShareSvg(ev, isSponsor) {
  const line = isSponsor ? "Proud sponsor." : "See you there.";
  const date = ev?.date || "Sat, 28 November 2026";
  const venue = ev?.venue || "Trishul Kalaabhavana, Davangere";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350">
<defs>
  <radialGradient id="g" cx="92%" cy="92%" r="80%">
    <stop offset="0%" stop-color="#E0A526" stop-opacity="0.32"/>
    <stop offset="50%" stop-color="#E0A526" stop-opacity="0.06"/>
    <stop offset="100%" stop-color="#E0A526" stop-opacity="0"/>
  </radialGradient>
</defs>
<rect width="1080" height="1350" fill="#0B0D10"/>
<rect width="1080" height="1350" fill="url(#g)"/>
<g font-family="IBM Plex Mono, monospace" fill="#CC252F" font-size="24" letter-spacing="4">
  <text x="80" y="120">BNI DAVANGERE REGION  |  RISSE 26</text>
</g>
<g font-family="Fraunces, serif" font-size="140" letter-spacing="-3">
  <text x="80" y="360" fill="#F7F4EE">Members Day</text>
  <text x="80" y="530" fill="#E23E47" font-style="italic">Davangere</text>
  <text x="80" y="700" fill="#F7F4EE">Region — 2026</text>
</g>
<g font-family="Inter, sans-serif" fill="#C2BCB3" font-size="32">
  <text x="80" y="810">${escapeXml(date)}</text>
  <text x="80" y="860">${escapeXml(venue)}</text>
</g>
<rect x="80" y="1000" width="120" height="4" fill="#CC252F"/>
<text x="80" y="1075" font-family="Fraunces, serif" fill="#F7F4EE" font-size="50">${line}</text>
<g font-family="IBM Plex Mono, monospace" fill="#8B8378" font-size="22" letter-spacing="3">
  <text x="80" y="1250">RISSE.BNIDAVANGERE.COM</text>
</g>
</svg>`;
}

function svgToPngBlob(svg) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1080; canvas.height = 1350;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, 1080, 1350);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(url);
        b ? resolve(b) : reject(new Error("toBlob failed"));
      }, "image/png", 0.92);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export default function PaymentSuccess() {
  const { regId } = useParams();
  const nav = useNavigate();
  const { dispatch } = useDemo();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let dead = false;
    publicApi.sync(regId)
      .then((r) => { if (!dead) setData(r); })
      .catch(() => publicApi.get(regId).then((r) => { if (!dead) setData(r); }).catch(() => !dead && setErr("We couldn't find that registration.")));
    return () => { dead = true; };
  }, [regId]);

  const isSponsor = data?.registration_type === "sponsor";
  const firstName = (data?.name || "").trim().split(" ")[0] || "there";
  const paidPaise = data?.payment?.amount_paise || 0;
  const ev = data?.event;

  const share = async () => {
    const url = "https://risse.bnidavangere.com/";
    const text = isSponsor
      ? `Proud to sponsor BNI Davangere Members Day 2026. Join us — ${url}`
      : `I'm attending BNI Davangere Members Day 2026 — join me. ${url}`;
    // Build a WhatsApp-ready share card (same visual language as the Hero "Save WhatsApp card")
    const svg = buildShareSvg(ev, isSponsor);
    try {
      const png = await svgToPngBlob(svg);
      const file = new File([png], "bni-members-day-2026.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        // Pass ONLY text (which already carries the URL once) to avoid WhatsApp doubling the link.
        await navigator.share({ files: [file], text, title: "BNI Members Day 2026" });
        return;
      }
    } catch (_) { /* fall through to desktop/web fallback */ }
    // Desktop / unsupported platforms: open WhatsApp Web with text only — URL appears exactly once.
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, "_blank", "noopener");
  };

  const openPortal = () => {
    // Force a fresh phone + OTP login every time the user enters the member portal from the success page.
    try { localStorage.removeItem(MEMBER_TOKEN_KEY); } catch (_) {}
    dispatch({ type: "SET_OTP_VERIFIED", value: false });
    nav("/member");
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const base = [
      [isSponsor ? "Sponsorship" : "Pass", data.tier?.name || "Members Day"],
      ["Paid", inr(paidPaise)],
      ["Date", ev?.date || "—"],
      ["Venue", ev?.venue || "—"],
    ];
    if (isSponsor && data.chapter) base.splice(1, 0, ["Chapter", data.chapter]);
    return base;
  }, [data, isSponsor, paidPaise, ev]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="text-center">
          <div className="mono-label" style={{ color: "var(--red-2)" }}>Not found</div>
          <h1 className="h-section text-3xl mt-3">{err}</h1>
          <button className="btn-red mt-6" onClick={() => nav("/register")} data-testid="success-back-btn">
            Back to registration <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="mono-label flex items-center gap-2" data-testid="success-loading">
          <Loader2 size={14} className="animate-spin" /> Confirming your payment…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen success-stage" data-testid="payment-success-page">
      <Toaster theme="dark" position="top-right" />
      <div className="success-sheen" aria-hidden="true" />
      <div className="relative max-w-[1060px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-start">
          <div>
            <Seal sponsor={isSponsor} />

            <motion.div variants={rise} custom={0} initial="hidden" animate="show">
              <div className="mono-label mt-7" style={{ color: isSponsor ? "var(--red-2)" : "var(--green)" }}>
                {isSponsor ? "Sponsorship confirmed" : "Registration confirmed"}
              </div>
            </motion.div>

            <motion.h1 variants={rise} custom={1} initial="hidden" animate="show"
              className="h-hero text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="success-headline">
              {isSponsor ? <>Thank you, {firstName}.</> : <>You're in, {firstName}.</>}
            </motion.h1>

            <motion.p variants={rise} custom={2} initial="hidden" animate="show"
              className="mt-5 max-w-[48ch]" style={{ color: "var(--body)" }}>
              {isSponsor
                ? <>Your <strong>{data.tier?.name || "sponsorship"}</strong> is confirmed and your GST tax invoice is on its way to <strong>{data.email}</strong>. You'll be featured on the sponsor wall, and your entry pass QR is ready below.</>
                : <>Your Members Day pass is live. The GST tax invoice has been emailed to <strong>{data.email}</strong> and your pass is on WhatsApp. Show the QR at the reception desk when you arrive.</>}
            </motion.p>

            <motion.dl variants={rise} custom={3} initial="hidden" animate="show"
              className="mt-10 grid grid-cols-2 gap-y-6 gap-x-8 max-w-[520px]">
              {rows.map(([k, v]) => (
                <div key={k}>
                  <dt className="mono-label" style={{ color: "var(--taupe)" }}>{k}</dt>
                  <dd className="mt-1.5 text-base">{v}</dd>
                </div>
              ))}
            </motion.dl>

            <motion.div variants={rise} custom={4} initial="hidden" animate="show" className="mt-11 flex flex-wrap gap-3">
              <button className="btn-red" onClick={openPortal} data-testid="success-portal-btn">
                Open my portal <ArrowRight size={14} />
              </button>
              <a className="btn-ghost" data-testid="success-invoice-btn" target="_blank" rel="noreferrer"
                href={`${API}/api/invoices/${data.id}.pdf`}>
                <Download size={14} /> Download invoice
              </a>
              <a className="btn-ghost" data-testid="success-calendar-btn" download="bni-members-day-2026.ics" href={icsHref(ev)}>
                <CalendarPlus size={14} /> Add to calendar
              </a>
              <button className="btn-ghost" onClick={share} data-testid="success-share-btn">
                <Share2 size={14} /> Share on WhatsApp
              </button>
            </motion.div>

            <motion.div variants={rise} custom={5} initial="hidden" animate="show"
              className="mono-label mt-7 inline-flex items-center gap-2" style={{ color: "var(--green)" }}>
              <MessageCircle size={13} /> Confirmation sent on WhatsApp &amp; email
            </motion.div>
          </div>

          {/* Pass / sponsor plate */}
          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="success-plate w-full" data-testid="success-pass-plate">
            <div className="flex items-center justify-between">
              <div className="mono-label" style={{ color: "var(--taupe)" }}>
                {isSponsor ? "Sponsor" : "Entry pass"}
              </div>
              {isSponsor && <Award size={15} style={{ color: "var(--red-2)" }} />}
            </div>
            <div className="font-serif text-2xl mt-2 leading-tight">{data.name}</div>
            <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
              {data.tier?.name || "Members Day"}{data.chapter ? ` · ${data.chapter}` : ""}
            </div>
            <div className="mt-6 bg-white p-3 inline-block">
              <RealQR regId={data.id} size={200} />
            </div>
            <div className="mono-label mt-4" data-testid="success-reg-id" style={{ color: "var(--taupe)" }}>
              {data.id.slice(0, 8).toUpperCase()} · {ev?.date || ""}
            </div>
            <div className="mono-label mt-5 hairline-t pt-4" style={{ color: "var(--muted)" }}>
              Check in at the reception desk on arrival — meals unlock after entry check-in.
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
